import type ApplicationInstance from '@ember/application/instance';

/**
 * URL pattern matching the same sources as isSupportedContentSourceUrl() in @favoris/core.
 * Keep in sync with packages/core/src/utils/url-detection.ts.
 */
const SUPPORTED_URL_RE =
  /^https?:\/\/(?:(?:www\.)?(?:tiktok\.com|instagram\.com|youtube\.com|youtu\.be)|(?:vm|vt)\.tiktok\.com|pca\.st|(?:www\.)?pocketcasts\.com|open\.spotify\.com\/episode|(?:www\.)?podcastindex\.org)\b/i;

/** Extract the first supported URL from arbitrary shared text (e.g. "Title https://..."). */
function extractSupportedUrl(text: string): string | null {
  const matches = text.match(/https?:\/\/[^\s"<>)]+/gi);
  if (!matches) return null;
  for (const match of matches) {
    if (SUPPORTED_URL_RE.test(match)) return match;
  }
  return null;
}

/** Check if an intent type is an image MIME type */
function isImageType(type: string | undefined): boolean {
  return typeof type === 'string' && type.startsWith('image/');
}

export function initialize(appInstance: ApplicationInstance): void {
  void (async () => {
    const shareIntentService = appInstance.lookup('service:share-intent');
    try {
      const { Capacitor } = await import('@capacitor/core');
      if (!Capacitor.isNativePlatform()) return;

      const { SendIntent } = await import('@mindlib-capacitor/send-intent');
      const result = await SendIntent.checkSendIntentReceived();

      // Do NOT call SendIntent.finish() here. That method calls activity.finish()
      // on SendIntentActivity, which triggers onPause() (which also calls finish()),
      // destroying the entire WebView within ~4ms — before the app can navigate to
      // /extract. The SendIntentActivity.java onPause() override already calls
      // finish() automatically when the user navigates away, which is the right time.

      // Handle image intents (type starts with "image/")
      if (isImageType(result.type) && result.url) {
        shareIntentService.pendingImageUri = result.url;
        shareIntentService.pendingImageType = result.type ?? 'image/jpeg';
        return;
      }

      // Handle text/URL intents
      const sharedText = result.url ?? result.description ?? result.title ?? '';
      if (!sharedText) return;

      const url = extractSupportedUrl(sharedText);
      if (!url) return; // unsupported source — ignore silently

      shareIntentService.pendingUrl = url;
    } catch {
      // Not a Capacitor environment or no send-intent received — normal launch.
    } finally {
      // Always notify, even on early return or error, so the application route
      // is never blocked waiting for a pending intent that never arrives.
      shareIntentService.notifyReady();
    }
  })();
}

export default { initialize };
